import { fromJS } from 'immutable';
import queryString from 'query-string';

// Redux
import { asyncConnect } from 'redux-connect';
import {
  getBriefsHash,
  isBriefsLoaded,
  isBriefsLoading,
  isFilterRoleSkillsLoaded,
  loadBriefs,
  loadFilterRoleSkills,
  loadOwnBriefs,
  loadOwnPitches,
} from 'redux/modules/projects2';
import { load as loadSuggestedRoles } from 'redux/modules/suggestedRoles2';
import { load as loadSuggestedSkills } from 'redux/modules/suggestedSkills2';
import { postBrief, clearPostedBrief } from 'redux/modules/projects';
import { getCitiesForCountry } from 'redux/modules/cities';
import { loadPartners } from 'redux/modules/partners';
import {
  loadExternalJobDays,
  loadMoreExternalJobs,
  retryExternalJobDays,
} from 'redux/modules/externalJobs';
import {
  setSelectedRoleCategory,
  clearSelectedRoleCategory,
} from 'redux/modules/selectedRoleCategory';
import { parseJobsPage } from './jobsPagination';
import { loadBoardExternalJobs } from './boardExternalJobs';
import { SSR_TIME_ZONE, getVisitorTimeZone } from './jobsTimeline';
import resolveJobsRoleSlug from './resolveJobsRoleSlug';
import { getBestMatchRedirect, parseBriefsFiltersCookie } from './jobsOrder';
import parseJobsStatus from 'components/JobsFilterPanel/parseJobsStatus';

import loadable from '@loadable/component';

const ProjectsBase = loadable(() => import('./ProjectsBase'));

const Projects = asyncConnect(
  [
    {
      promise: async ({
        store: { dispatch, getState },
        match: {
          params: { role, city, country },
        },
        location,
        helpers,
      }) => {
        // Full page loads of bare /jobs go straight to Best match when this
        // user last used it, without loading the listing first. Only the
        // server render has the request's cookies.
        if (helpers && typeof helpers.requestCookie === 'string') {
          const redirect = getBestMatchRedirect({
            pathname: location.pathname,
            search: location.search,
            saved: parseBriefsFiltersCookie(helpers.requestCookie),
            user: getState().auth.user,
          });
          if (redirect) {
            helpers.redirectTo = redirect;
            return Promise.resolve();
          }
        }

        const paid = location.pathname.includes('/jobs');
        const promises = [];
        const hasRoleParam = typeof role !== 'undefined';

        // Role pages must resolve slug → display name before loadBriefs so the
        // briefs hash matches client hydration (parseRoles / getHash). Await
        // roleSkills first in that case; otherwise load in parallel as before.
        if (!isFilterRoleSkillsLoaded(getState(), paid)) {
          if (hasRoleParam) {
            await dispatch(loadFilterRoleSkills(paid));
          } else {
            promises.push(dispatch(loadFilterRoleSkills(paid)));
          }
        }

        const roleSkills =
          (getState().projects2.filterRoleSkills &&
            getState().projects2.filterRoleSkills[paid.toString()] &&
            getState().projects2.filterRoleSkills[paid.toString()]
              .roleSkills) ||
          [];

        // Match ProjectsBase.parseRoles: query roles first, then path role name
        const filteredRoles = [];
        const parsedRoles = queryString.parse(location.search).roles;

        if (typeof parsedRoles === 'string') {
          filteredRoles.push(parsedRoles);
        } else if (typeof parsedRoles === 'object') {
          parsedRoles.forEach((r) => filteredRoles.push(r));
        }

        if (hasRoleParam) {
          const resolvedRole = resolveJobsRoleSlug(roleSkills, role) || role;
          if (!filteredRoles.includes(resolvedRole)) {
            filteredRoles.push(resolvedRole);
          }
        }

        // Parse experienceLevels from query string
        const filteredExperienceLevels = [];
        const parsedExperienceLevels = queryString.parse(
          location.search
        ).experienceLevel;

        if (typeof parsedExperienceLevels === 'string') {
          // 1 experience level filtered - represented as string
          filteredExperienceLevels.push(parsedExperienceLevels);
        } else if (typeof parsedExperienceLevels === 'object') {
          // multiple experienceLevels filtered - represented as string[]
          parsedExperienceLevels.forEach((l) =>
            filteredExperienceLevels.push(l)
          );
        }

        const experienceLevels = [
          { type: 'Amateur', isChecked: false },
          { type: 'Beginner', isChecked: false },
          { type: 'Junior', isChecked: false },
          { type: 'Mid-level', isChecked: false },
          { type: 'Senior', isChecked: false },
          { type: 'Expert', isChecked: false },
        ];

        // Update `isChecked` for the experience levels found in the query string
        filteredExperienceLevels.forEach((l) => {
          const parsedExperienceLevel = experienceLevels.find(
            (el) => el.type === l
          );

          if (typeof parsedExperienceLevel !== 'undefined') {
            parsedExperienceLevel.isChecked = true;
          }
        });

        // Parse hiringCapacities from query string
        const filteredHiringCapacities = [];
        const parsedHiringCapacities = queryString.parse(
          location.search
        ).hiringCapacity;

        if (typeof parsedHiringCapacities === 'string') {
          // 1 hiring capacity filtered - represented as string
          filteredHiringCapacities.push(parsedHiringCapacities);
        } else if (typeof parsedHiringCapacities === 'object') {
          // multiple hiring capacities filtered - represented as string[]
          parsedHiringCapacities.forEach((h) =>
            filteredHiringCapacities.push(h)
          );
        }

        const hiringCapacities = [
          { type: 'personal', isChecked: false },
          { type: 'commercial', isChecked: false },
        ];

        // Update `isChecked` for the hiring capacities found in the query string
        filteredHiringCapacities.forEach((h) => {
          const parsedHiringCapacity = hiringCapacities.find(
            (hc) => hc.type === h
          );

          if (typeof parsedHiringCapacity !== 'undefined') {
            parsedHiringCapacity.isChecked = true;
          }
        });

        const jobLocationText = city ? `${city}, ${country}` : country;

        // Parse filters from query string (open-to-applications defaults ON)
        const query = queryString.parse(location.search);
        const { status, searchTerm } = query;
        const jobStatusOpen = parseJobsStatus(status);
        // Booleans, as ProjectsBase parses them: `?remote=1` must hash the
        // same as the board's `remote: true` or it never finds the results.
        const remote = !!query.remote;
        const internal = !!query.internal;
        const page = parseJobsPage(location.search);

        // Load Briefs — SSR deep links (?page=N) fetch the first N batches
        // in one request (limit = page * 10) and set pagesLoaded to page.
        // Client-side pagination still loads batch-by-batch via ProjectsBase.
        // Skip when already loaded or in-flight (ProjectsBase may have
        // started the same request on the filter state change).
        const briefsArgs = [
          paid,
          filteredRoles,
          experienceLevels,
          hiringCapacities,
          jobStatusOpen,
          remote,
          internal,
          jobLocationText,
          searchTerm,
        ];
        // Jobs from other sites for the days those Twine jobs cover, so day
        // and section headings are in the first HTML. The server groups days
        // in a fixed zone; the browser uses the visitor's. A failure here
        // must not fail the page; the board can still load them later.
        const loadExternal = () => {
          if (
            !paid ||
            queryString.parse(location.search).order === 'best-match'
          ) {
            return null;
          }
          return Promise.resolve(
            loadBoardExternalJobs(dispatch, getState(), {
              hash: getBriefsHash(...briefsArgs),
              filters: {
                roles: filteredRoles,
                experienceLevels,
                hiringCapacities,
                remote,
                internal,
                jobLocationText,
                searchTerm,
              },
              timeZone:
                typeof window === 'undefined'
                  ? SSR_TIME_ZONE
                  : getVisitorTimeZone(),
            })
          ).catch(() => null);
        };

        if (
          !isBriefsLoaded(getState(), ...briefsArgs) &&
          !isBriefsLoading(getState(), ...briefsArgs)
        ) {
          promises.push(
            dispatch(
              loadBriefs(page, ...briefsArgs, {
                limit: page * 10,
                apiPage: 1,
              })
            ).then(loadExternal)
          );
        } else {
          promises.push(loadExternal());
        }

        return Promise.all(promises);
      },
    },
  ],
  (state) => ({
    user: state.auth.user,
    projects: fromJS(state.projects2),
    suggestedRoles: fromJS(state.suggestedRoles2),
    suggestedSkills: fromJS(state.suggestedSkills2),
    briefsEntities: fromJS(state.entities.briefs),
    roleEntities: fromJS(state.entities.roles),
    skillEntities: fromJS(state.entities.skills),
    userEntities: fromJS(state.entities.users),
    posted: state.projects.posted,
    posting: state.projects.posting,
    cities: state.cities,
    loadingPartners: state.partners.loadingPartners,
    loadedPartners: state.partners.loadedPartners,
    partners: state.partners.partners,
    externalJobs: state.externalJobs,
  }),
  {
    loadBriefs,
    loadExternalJobDays,
    loadMoreExternalJobs,
    retryExternalJobDays,
    loadFilterRoleSkills,
    loadPartners,
    loadOwnBriefs,
    loadOwnPitches,
    loadSuggestedRoles,
    loadSuggestedSkills,
    clearPostedBrief,
    postBrief,
    getCitiesForCountry,
    setSelectedRoleCategory,
    clearSelectedRoleCategory,
  }
)(ProjectsBase);

export default Projects;
